import React, {
  FCC,
  ForwardRefExoticComponent,
  useCallback,
  useState,
} from 'react'
import styled, { CSSProperties, useTheme } from 'styled-components'
import { animated, useTransition } from 'react-spring'

import {
  CardNumberElement,
  CardNumberElementProps,
} from '@stripe/react-stripe-js'

import { PaymentLogo } from '../../PaymentLogo'
import { Input, InputProps } from '../Input'
import { commonStyles, invalidStyles } from './commonStyles'
import { inputBaseStyles } from '../Input.styles'

const AnimatedDiv = animated.div as ForwardRefExoticComponent<{
  children: React.ReactElement
  style: CSSProperties
}>

const INITIAL_ANIMATION_STATE = {
  transform: 'scale(0)',
  marginRight: '-40px' /* Width of the card icon + the flex gap */,
  opacity: 0,
}
const FINAL_ANIMATION_STATE = {
  transform: 'scale(1)',
  marginRight: '0px',
  opacity: 1,
}

export interface CardNumberInputProps extends CardNumberElementProps {
  disabled?: boolean
  invalid?: boolean
}

const ANIMATION_STATES = {
  initial: null,
  from: INITIAL_ANIMATION_STATE,
  enter: FINAL_ANIMATION_STATE,
  leave: INITIAL_ANIMATION_STATE,
  config: { mass: 0.5, tension: 400, friction: 20 },
  unique: true,
  trail: 0,
}

const CardWrapper = styled.div`
  width: auto;
  height: 20px;
  user-select: none;
  overflow: hidden;

  display: flex;
  gap: 12px;
`

const CardLogo = styled(PaymentLogo)`
  :not(:last-child) {
    margin-right: 12px;
  }
`

const CardInputWrapper = styled(Input).attrs<InputProps>({
  className: 'StripeElement',
})<InputProps>`
  ${inputBaseStyles};

  display: flex;
  align-items: center;
`

const BaseCardNumberInput = styled(CardNumberElement)`
  flex: 1;

  &::placeholder {
    color: ${({ theme }) => theme.colors.foreground.secondary.C};
  }
`

const SUPPORT_CREDIT_CARDS = ['amex', 'mastercard', 'visa'] as const

export const CardNumberInput: FCC<CardNumberInputProps> = ({
  className,
  disabled,
  onChange,
  invalid,
  onFocus,
  onBlur,
  ...props
}) => {
  const theme = useTheme()
  const [cardBrand, setCardBrand] = useState('unknown')
  const [isInputFocused, setIsInputFocused] = useState(false)

  const handleCardNumberOnChange = useCallback<
    NonNullable<CardNumberElementProps['onChange']>
  >(
    callbackValues => {
      const { brand } = callbackValues

      cardBrand !== brand && setCardBrand(brand)
      onChange && onChange(callbackValues)
    },
    [cardBrand, onChange]
  )

  const transitions = useTransition(
    cardBrand === 'unknown' ? SUPPORT_CREDIT_CARDS : [cardBrand],
    i => i,
    ANIMATION_STATES
  )

  const handleTransition = useCallback(
    ({ item, key, props }: (typeof transitions)[number]) => {
      return (
        <AnimatedDiv key={key} style={props}>
          <CardLogo className={item} brand={item} key={item} />
        </AnimatedDiv>
      )
    },
    []
  )

  return (
    <CardInputWrapper
      className={className || 'StripeElement'}
      disabled={disabled ?? false}
      stripeFakeFocus={isInputFocused}
      invalid={invalid}
      as="div"
    >
      <BaseCardNumberInput
        {...props}
        options={{
          style: invalid ? invalidStyles(theme) : commonStyles(theme),
        }}
        onFocus={event => {
          onFocus?.(event)
          setIsInputFocused(true)
        }}
        onBlur={event => {
          setIsInputFocused(false)
          onBlur?.(event)
        }}
        onChange={handleCardNumberOnChange}
      />
      <CardWrapper>{transitions.map(handleTransition)}</CardWrapper>
    </CardInputWrapper>
  )
}
